// Eingaben: Gehäusetasten (Pointer + Auto-Repeat), Wischgesten auf dem Display,
// Tastatur. Alles wird auf einen einzigen press(btn)-Dispatch abgebildet; die
// Bedeutung je Zustand entscheidet der Handler (main.ts).

export type Button = 'left' | 'right' | 'up' | 'down' | 'a' | 'b' | 'start' | 'select';

export interface InputHandler {
  /** Ein Tastendruck (auch jeder Auto-Repeat-Tick). */
  press(btn: Button): void;
  /** Langer Druck auf SELECT → Optionen. */
  selectLong(): void;
  /** true, wenn Gesten den Stein direkt steuern (Spiel läuft, keine Optionen). */
  isInteractive(): boolean;
  /** Erste Nutzerinteraktion → Audio entsperren. */
  unlock(): void;
}

const REPEAT_DELAY = 230;
const REPEAT_INTERVAL = 105;
const LONG_PRESS = 500;
const REPEATABLE: ReadonlySet<Button> = new Set<Button>(['left', 'right', 'down']);

/** Verzögerter, dann getakteter Auto-Repeat für gehaltene Tasten. */
class AutoRepeat {
  private delayId: number | undefined;
  private intervalId: number | undefined;

  start(fire: () => void): void {
    this.stop();
    fire();
    this.delayId = window.setTimeout(() => {
      this.intervalId = window.setInterval(fire, REPEAT_INTERVAL);
    }, REPEAT_DELAY);
  }

  stop(): void {
    if (this.delayId !== undefined) window.clearTimeout(this.delayId);
    if (this.intervalId !== undefined) window.clearInterval(this.intervalId);
    this.delayId = undefined;
    this.intervalId = undefined;
  }
}

export class InputController {
  private readonly repeat = new AutoRepeat();

  constructor(
    private readonly root: ParentNode,
    private readonly screen: HTMLElement,
    private readonly handler: InputHandler,
  ) {
    this.bindButtons();
    this.bindGestures();
    this.bindKeyboard();
  }

  private bindButtons(): void {
    const buttons = this.root.querySelectorAll<HTMLElement>('[data-btn]');
    buttons.forEach((el) => {
      const btn = el.dataset.btn as Button;
      if (btn === 'select') {
        this.bindSelect(el);
        return;
      }
      el.addEventListener(
        'pointerdown',
        (e) => {
          e.preventDefault();
          this.handler.unlock();
          if (REPEATABLE.has(btn)) {
            this.repeat.start(() => this.handler.press(btn));
          } else {
            this.handler.press(btn);
          }
        },
        { passive: false },
      );
      const stop = (e: Event) => {
        e.preventDefault();
        if (REPEATABLE.has(btn)) this.repeat.stop();
      };
      el.addEventListener('pointerup', stop, { passive: false });
      el.addEventListener('pointerleave', stop, { passive: false });
      el.addEventListener('pointercancel', stop, { passive: false });
      el.addEventListener('contextmenu', (e) => e.preventDefault());
    });
  }

  private bindSelect(el: HTMLElement): void {
    let timer: number | undefined;
    let longFired = false;
    el.addEventListener(
      'pointerdown',
      (e) => {
        e.preventDefault();
        this.handler.unlock();
        longFired = false;
        timer = window.setTimeout(() => {
          longFired = true;
          this.handler.selectLong();
        }, LONG_PRESS);
      },
      { passive: false },
    );
    const up = (e: Event) => {
      e.preventDefault();
      if (timer !== undefined) window.clearTimeout(timer);
      if (!longFired) this.handler.press('select');
    };
    const cancel = () => {
      if (timer !== undefined) window.clearTimeout(timer);
    };
    el.addEventListener('pointerup', up, { passive: false });
    el.addEventListener('pointerleave', cancel);
    el.addEventListener('pointercancel', cancel);
    el.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private bindGestures(): void {
    const STEP_PX = 24; // eine Spalte pro ~24 px horizontaler Bewegung
    const TAP_PX = 18;
    const TAP_MS = 300;
    const SWIPE_DOWN = 40;

    let sx = 0;
    let sy = 0;
    let lastX = 0;
    let t0 = 0;
    let accX = 0;
    let steppedH = false;
    let active = false;

    this.screen.addEventListener(
      'pointerdown',
      (e) => {
        e.preventDefault();
        this.handler.unlock();
        active = true;
        sx = lastX = e.clientX;
        sy = e.clientY;
        t0 = Date.now();
        accX = 0;
        steppedH = false;
        this.screen.setPointerCapture?.(e.pointerId);
      },
      { passive: false },
    );

    this.screen.addEventListener(
      'pointermove',
      (e) => {
        if (!active || !this.handler.isInteractive()) return;
        accX += e.clientX - lastX;
        lastX = e.clientX;
        while (Math.abs(accX) >= STEP_PX) {
          const dir = accX > 0 ? 'right' : 'left';
          this.handler.press(dir);
          accX -= accX > 0 ? STEP_PX : -STEP_PX;
          steppedH = true;
        }
      },
      { passive: false },
    );

    const end = (e: PointerEvent) => {
      if (!active) return;
      active = false;
      const dx = e.clientX - sx;
      const dy = e.clientY - sy;
      const dt = Date.now() - t0;

      if (!this.handler.isInteractive()) {
        // ready/paused/over → Tippen startet bzw. setzt fort.
        this.handler.press('start');
        return;
      }
      if (steppedH) return; // horizontale Bewegung bereits behandelt
      if (Math.abs(dx) < TAP_PX && Math.abs(dy) < TAP_PX && dt < TAP_MS) {
        this.handler.press('up'); // Tap → drehen
      } else if (dy > SWIPE_DOWN && Math.abs(dy) > Math.abs(dx)) {
        this.handler.press('a'); // nach unten → Hard-Drop
      } else if (dy < 0 && Math.abs(dy) > Math.abs(dx)) {
        this.handler.press('up'); // nach oben → drehen
      }
    };
    this.screen.addEventListener('pointerup', end, { passive: false });
    this.screen.addEventListener('pointercancel', () => {
      active = false;
    });
    this.screen.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  private bindKeyboard(): void {
    const map: Record<string, Button> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowDown: 'down',
      ArrowUp: 'up',
      ' ': 'a',
      Enter: 'start',
      p: 'start',
      P: 'start',
    };
    window.addEventListener('keydown', (e) => {
      const btn = map[e.key];
      if (!btn) return;
      e.preventDefault();
      this.handler.unlock();
      this.handler.press(btn);
    });
  }
}
